import { getAssetPath } from '@stencil/core';
import { z } from 'zod';


export const TranslationsProps = z.object({
  locale: z.string().optional(),
  defaultLocale: z.string().default('en'),
  queryParam: z.string().optional(),
  localStorageKey: z.string().optional(),
})
export type TranslationsProps = z.infer<typeof TranslationsProps>;

export const CustomTranslations = z.object({
  componentName: z.string(),
  locale: z.string(),
  translations: z.map(z.string(), z.string()),
})
export type CustomTranslations = z.infer<typeof CustomTranslations>;

const Locale = z.string();
type Locale = z.infer<typeof Locale>;

const ComponentName = z.string();
type ComponentName = z.infer<typeof ComponentName>;

const TranslationsMap = z.map(ComponentName, z.map(Locale, z.map(z.string(), z.string())));
type TranslationsMap = z.infer<typeof TranslationsMap>;

class TranslationService {
  private static instance: TranslationService;
  private locale: string = 'en';
  private defaultLocale: string = 'en';
  public translations: TranslationsMap = new Map<ComponentName, Map<Locale, Map<string, string>>>();

  public static getInstance(): TranslationService {
    if (!TranslationService.instance) {
      TranslationService.instance = new TranslationService();
    }
    return TranslationService.instance;
  }

  public init(props: TranslationsProps): void {
    const propsObj = TranslationsProps.parse(props);
    if (propsObj.locale) {
      this.locale = propsObj.locale;
    }
    else if (propsObj.queryParam) {
      const urlParams = new URLSearchParams(window.location.search);
      this.locale = urlParams.get(propsObj.queryParam) || propsObj.defaultLocale;
    }
    else if (propsObj.localStorageKey) {
      const storedLocale = localStorage.getItem(propsObj.localStorageKey);
      this.locale = storedLocale || propsObj.defaultLocale;
    }
    else {
      this.locale = propsObj.defaultLocale;
    }
    this.defaultLocale = propsObj.defaultLocale;
  }

  public getLocale(): string {
    return this.locale;
  }
  public setLocale(locale: string): void {
    this.locale = locale;
  }

  public getDefaultLocale(): string {
    return this.defaultLocale;
  }
  public setDefaultLocale(locale: string): void {
    this.defaultLocale = locale;
  }

  public async initComponentLoad(componentName: string): Promise<void> {
    await this.loadTranslations(componentName, this.locale);
    if (this.defaultLocale !== this.locale) await this.loadTranslations(componentName, this.defaultLocale);
  }

  public async loadTranslations(componentName: string, locale: string) {
    if (this.translations.has(componentName) && this.translations.get(componentName)?.has(locale)) {
      return;
    }

    try {
      const response = await fetch(getAssetPath(`./components/${componentName}/translations/${locale}.json`));
      if (!response.ok) {
        throw new Error(`Failed to load translations for ${componentName} in locale ${locale}`);
      }
      const jsonObject: Record<string, string> = await response.json();
      const translationsMap = new Map<string, string>(Object.entries(jsonObject));
      if (!this.translations.has(componentName)) {
        this.translations.set(componentName, new Map<Locale, Map<string, string>>());
      }
      this.translations.get(componentName)?.set(locale, translationsMap);
    }
    catch (error) {
      console.error(`Error loading translations for ${componentName} in locale ${locale}:`, error);
    }
  }

  public getTranslation(componentName: string, key: string, locale?: string): string {
    if (!locale) {
      locale = this.locale;
    }

    const componentTransitions = this.translations.get(componentName);

    return componentTransitions?.get(locale)?.get(key) ||
      componentTransitions?.get(this.defaultLocale)?.get(key) ||
      key;
  }

}

export const translationService = TranslationService.getInstance();
